import { useAuth } from "../context/AuthContext";

/**
 * This map is the front-end mirror of the GROUP_PERMISSIONS dictionary in the Python backend.
 * It is the single source of truth for all UI-level permission checks.
 * IMPORTANT: If you change permissions in admin_user_auth.py, you must update this map to match.
 */
const permissionMap = {
  SuperAdmin: [
    "admin_user:manage",
    "admin_user:view",
    "users:read-only",
    "coupons:manage",
    "coupons:view",
    "coupons:create-modify",
    "coupons:activate",
    "coupons:assign-admin-user",
    "invoices:manage",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:special-set-coin-config",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:return-lead-coins",
    "cx:update-lead-status",
    "cx:seller-account-login",
    "cx:update-banned-content",
    "cx:adjust-coins",
    "users:update-seller-status",
    "users:update-buyer-status",
    "marketing:landing-pages:manage",
    "content:seo:manage",
    "content:categories:manage",
    "misc",
    "users:manage"
  ],
  SalesAdmin: [
    "admin_user:view",
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "coupons:assign-admin-user",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:seller-account-login",
  ],
  SalesTL: [
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:seller-account-login",
  ],
  SalesAgent: [
    "coupons:activate",
    "users:read-only",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:seller-account-login",
  ],
  MarketingAdmin: [
    "admin_user:view",
    "users:read-only",
    "coupons:manage",
    "coupons:view",
    "coupons:create-modify",
    "coupons:activate",
    "coupons:assign-admin-user",
    "invoices:view",
    "sales:give-free-response",
    "cx:update-lead-status",
    "cx:seller-account-login",
    "cx:update-banned-content",
    "users:update-seller-status",
    "users:update-buyer-status",
    "marketing:landing-pages:manage",
    "content:seo:manage",
    "content:categories:manage",
    "misc",
  ],
  SEOUser: ["users:read-only", "coupons:view", "content:seo:manage"],
  CategoryManager: [
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "sales:give-free-response",
    "content:categories:manage",
  ],
  CXAdmin: [
    "admin_user:view",
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "coupons:assign-admin-user",
    "invoices:manage",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:special-set-coin-config",
    "sales:manages-seller-accounts",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:return-lead-coins",
    "cx:update-lead-status",
    "cx:seller-account-login",
    "cx:update-banned-content",
    "cx:adjust-coins",
    "users:update-seller-status",
    "users:update-buyer-status",
    "cx:manage-reviews",
    "misc",
    "users:manage"
  ],
  CXTL: [
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "invoices:manage",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "cx:return-lead-coins",
    "cx:update-lead-status",
    "cx:seller-account-login",
    "cx:adjust-coins",
    "users:update-seller-status",
    "users:update-buyer-status",
    "cx:manage-reviews",
    "users:manage"
  ],
  CXAgent: [
    "users:read-only",
    "coupons:activate",
    "invoices:view",
    "sales:give-free-response",
    "cx:return-lead-coins",
    "cx:update-lead-status",
    "cx:seller-account-login",
    "users:update-seller-status",
    "users:update-buyer-status",
    "cx:manage-reviews",
  ],
  AffiliateManager: [
    "users:read-only",
    "coupons:view",
    "invoices:manage",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:give-free-response",
    "notes:create",
    "notes:edit",
    "notes:delete",
    "affiliate:view",
    "affiliate:manage",
    "contractor:create",
    "contractor:manage",
  ],
  AffiliateAgent: [
    "users:read-only",
    "coupons:view",
    "affiliate:view",
    "affiliate:manage",
  ],
  SalesContractor: [
    "users:read-only",
    "sales-contractor:general",
    "sales:create-special-set",
    "sales:give-free-response",
    "sales-contractor:special-set-discount",
    "sales:custom-discount-special-set",
    "sales:view-leads-for-calling",
    "coupons:activate",
    "coupons:view",
  ],
  EnterpriseSalesManager: [
    "admin_user:view",
    "users:read-only",
    "coupons:view",
    "coupons:activate",
    "invoices:view",
    "sales:create-special-set",
    "sales:custom-discount-special-set",
    "sales:special-set-coin-config",
    "sales:give-free-response",
    "sales:view-leads-for-calling",
    "sales:manages-seller-accounts",
    "cx:seller-account-login",
    "notes:create",
    "notes:edit",
    "notes:delete",
    "affiliate:view",
    "affiliate:manage",
    "contractor:create",
    "contractor:manage",
  ],
};

/**
 * A custom hook to check if the current user has a specific permission.
 * @returns {{hasPermission: (permission: string) => boolean, userGroup: string | null}}
 * An object with the hasPermission function and the user's group.
 */
export const usePermissions = () => {
  const { user } = useAuth();

  const userGroup = user?.groups?.[0] || null;

  const hasPermission = (requiredPermission) => {
    if (!userGroup) {
      return false;
    }

    // SuperAdmins have all permissions implicitly
    if (userGroup === "SuperAdmin") {
      return true;
    }

    const userPermissions = permissionMap[userGroup] || [];
    return userPermissions.includes(requiredPermission);
  };

  return { hasPermission, userGroup };
};
